import React, { useMemo, useState, useEffect, useRef } from 'react';
import { Modal, Input, message, Select } from 'antd';
import type { InputRef } from 'antd';
import { useWarehouseContext } from '../../../../contexts/WarehouseContext';
import { useGetAllAccessHubAndWarehousesForRole } from '../../../../hooks/useWarehouseQuery';
import { SiteType } from '../../../../graphql/types/SiteType';
import i18n, { t } from '../../../../i18n';

interface AddLocationModalProps {
    visible: boolean;
    onCancel: () => void;
    onOk: (locationNoList: string[], warehouseId: number) => void;
    loading?: boolean;
}

const AddLocationModal: React.FC<AddLocationModalProps> = ({
    visible,
    onCancel,
    onOk,
    loading = false
}) => {
    const [locationLines, setLocationLines] = useState<string[][]>([['', '', '', '']]);
    const [focusedLineIndex, setFocusedLineIndex] = useState<number | null>(0);
    const [focusedPartIndex, setFocusedPartIndex] = useState<number>(0);
    const lineInputRefs = useRef<Array<Array<InputRef | null>>>([]);
    const { currentWarehouse } = useWarehouseContext();
    const { warehouses: allSites } = useGetAllAccessHubAndWarehousesForRole({ fetchPolicy: 'cache-first' });
    const [selectedWarehouseId, setSelectedWarehouseId] = useState<number | undefined>(undefined);

    const hubName = useMemo(() => {
        if (!currentWarehouse) return '';
        if (Number(currentWarehouse.siteType) === SiteType.HUB) {
            return currentWarehouse.name || '';
        }
        const hub = allSites.find(
            (site: any) => Number(site.siteType) === SiteType.HUB && Number(site.id) === Number(currentWarehouse.parentSiteId)
        );
        return hub?.name || '';
    }, [allSites, currentWarehouse]);

    const warehouseOptions = useMemo(() => {
        if (!currentWarehouse) {
            return [];
        }
        if (Number(currentWarehouse.siteType) === SiteType.WAREHOUSE) {
            return [{
                value: Number(currentWarehouse.id),
                label: currentWarehouse.name || currentWarehouse.id
            }];
        }
        return allSites
            .filter((site: any) => Number(site.siteType) === SiteType.WAREHOUSE && Number(site.parentSiteId) === Number(currentWarehouse.id))
            .map((site: any) => ({
                value: Number(site.id),
                label: site.name || site.id
            }));
    }, [allSites, currentWarehouse]);

    useEffect(() => {
        if (!visible) {
            setLocationLines([['', '', '', '']]);
            setSelectedWarehouseId(undefined);
            setFocusedLineIndex(0);
            setFocusedPartIndex(0);
            return;
        }
        if (!currentWarehouse) {
            return;
        }
        if (Number(currentWarehouse.siteType) === SiteType.WAREHOUSE && currentWarehouse.id) {
            setSelectedWarehouseId(Number(currentWarehouse.id));
            return;
        }
        if (warehouseOptions.length > 0 && !selectedWarehouseId) {
            setSelectedWarehouseId(warehouseOptions[0].value);
        }
    }, [visible]);

    const handleOk = () => {
        // 将每行的4个输入框用-连接起来
        const locationNoList = locationLines
            .map((parts) => {
                const filtered = parts.filter(p => p.trim().length > 0);
                return filtered.length > 0 ? filtered.join('-') : '';
            })
            .filter((line) => line.length > 0);

        if (locationNoList.length === 0) {
            message.warning(t('wms.locationManagement.messages.locationNoRequired'));
            return;
        }

        if (locationNoList.length > 50) {
            message.warning(t('wms.locationManagement.messages.maxLocations'));
            return;
        }

        // 验证格式：最多20个字符（不包括-），最多4组
        const invalid = locationNoList.find((item) => {
            const parts = item.split('-');
            const totalChars = parts.join('').length;
            return totalChars > 20 || parts.length > 4;
        });
        if (invalid) {
            message.warning(t('wms.locationManagement.messages.maxLocationLength'));
            return;
        }

        if (!selectedWarehouseId) {
            message.warning(t('wms.locationManagement.messages.selectWarehouse'));
            return;
        }

        onOk(locationNoList, selectedWarehouseId);
    };

    const rowHeight = 36;
    const minVisibleRows = 8;
    const containerHeight = rowHeight * minVisibleRows;

    const isForeign = !i18n.language?.startsWith('zh');
    const labelClassName = isForeign
        ? 'mb-1 whitespace-nowrap text-gray-500'
        : 'mr-2 whitespace-nowrap text-gray-500';
    const rowClassName = isForeign
        ? 'flex flex-col'
        : 'flex items-center';

    const handlePartChange = (lineIndex: number, partIndex: number, value: string) => {
        // 只允许输入数字和字母，过滤掉其他字符（包括空格）
        const filteredValue = value.replace(/[^a-zA-Z0-9]/g, '');
        
        // 使用过滤后的值继续处理（允许空字符串，用于删除操作）
        value = filteredValue;
        
        // 支持输入数字和字母，不限制每个输入框的字符数，但总体限制20个字符
        const next = [...locationLines];
        const currentParts = [...next[lineIndex]];
        
        // 计算其他输入框已有的字符数（不包括当前输入框）
        const otherParts = currentParts.filter((_, i) => i !== partIndex);
        const totalOtherChars = otherParts.join('').length;
        
        // 如果输入为空（删除操作），直接更新当前输入框为空，允许删除
        if (value.length === 0) {
            currentParts[partIndex] = '';
            next[lineIndex] = currentParts;
            setLocationLines(next);
            return;
        }
        
        // 计算剩余可输入的字符数（总共不超过20个字符）
        const remainingChars = 20 - totalOtherChars;
        
        // 如果剩余可输入字符数为0或负数，提示错误（但删除操作已经在上面处理了）
        if (remainingChars <= 0) {
            message.warning(t('wms.locationManagement.messages.maxLocationLength'));
            return;
        }
        
        // 限制当前输入框的输入不超过剩余可输入的字符数
        const maxCharsForCurrent = remainingChars;
        const finalValue = value.slice(0, maxCharsForCurrent);
        
        currentParts[partIndex] = finalValue;
        
        // 如果输入超过了剩余字符数，提示错误
        if (value.length > maxCharsForCurrent) {
            message.warning(t('wms.locationManagement.messages.maxLocationLength'));
        }
        
        next[lineIndex] = currentParts;
        setLocationLines(next);
    };

    const handlePartKeyDown = (lineIndex: number, partIndex: number, event: React.KeyboardEvent<HTMLInputElement>) => {
        // 禁止输入空格键
        if (event.key === ' ' || event.key === 'Space' || event.code === 'Space') {
            event.preventDefault();
            event.stopPropagation();
            return;
        }
        
        // 禁止输入非数字和字母的字符
        const key = event.key;
        if (key.length === 1 && !/^[a-zA-Z0-9]$/.test(key)) {
            event.preventDefault();
            event.stopPropagation();
            return;
        }
        
        if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'z') {
            event.preventDefault();
            return;
        }
        
        const inputElement = event.currentTarget;
        const cursorPosition = inputElement.selectionStart || 0;
        const textLength = locationLines[lineIndex][partIndex].length;
        
        // Backspace：如果输入框为空且光标在开头，跳转到上一个输入框
        // 否则允许正常删除（删除最后一个字符后，光标保持在当前输入框）
        if (event.key === 'Backspace') {
            if (textLength === 0 && cursorPosition === 0 && partIndex > 0) {
                event.preventDefault();
                setTimeout(() => {
                    const prevInput = lineInputRefs.current[lineIndex]?.[partIndex - 1];
                    if (prevInput?.input) {
                        const prevValue = locationLines[lineIndex][partIndex - 1];
                        prevInput.focus();
                        // 将光标移到上一个输入框的末尾
                        (prevInput.input as HTMLInputElement).setSelectionRange(prevValue.length, prevValue.length);
                    }
                }, 0);
                return;
            }
            // 如果输入框有内容，允许正常删除，不跳转
            return;
        }
        
        // 右箭头：如果光标在末尾，或者按右箭头，跳转到下一个输入框
        if (event.key === 'ArrowRight') {
            if (cursorPosition === textLength && partIndex < 3) {
                event.preventDefault();
                setTimeout(() => {
                    const nextInput = lineInputRefs.current[lineIndex]?.[partIndex + 1];
                    nextInput?.focus?.();
                    // 将光标移到下一个输入框的开头
                    if (nextInput?.input) {
                        (nextInput.input as HTMLInputElement).setSelectionRange(0, 0);
                    }
                }, 0);
                return;
            }
        }
        
        // 左箭头：如果光标在开头，跳转到上一个输入框
        if (event.key === 'ArrowLeft') {
            if (cursorPosition === 0 && partIndex > 0) {
                event.preventDefault();
                setTimeout(() => {
                    const prevInput = lineInputRefs.current[lineIndex]?.[partIndex - 1];
                    prevInput?.focus?.();
                    // 将光标移到上一个输入框的末尾
                    if (prevInput?.input) {
                        const prevTextLength = locationLines[lineIndex][partIndex - 1].length;
                        (prevInput.input as HTMLInputElement).setSelectionRange(prevTextLength, prevTextLength);
                    }
                }, 0);
                return;
            }
        }
        
        // 输入满5个字符时，自动跳转到下一个输入框
        if (event.key.length === 1 && !event.ctrlKey && !event.metaKey && textLength === 5 && partIndex < 3) {
            // 这个逻辑在 handlePartChange 中处理，这里不需要额外处理
        }
        
        // Enter键：在任意输入框按下均可换行（不要求填满四个格）
        if (event.key === 'Enter') {
            event.preventDefault();
            const hasContent = locationLines[lineIndex].some(p => p.trim().length > 0);
            if (!hasContent) {
                return;
            }
            if (locationLines.length >= 50) {
                message.warning(t('wms.locationManagement.messages.maxLocations'));
                return;
            }
            const next = [...locationLines];
            next.splice(lineIndex + 1, 0, ['', '', '', '']);
            setLocationLines(next);
            setTimeout(() => {
                const nextInput = lineInputRefs.current[lineIndex + 1]?.[0];
                nextInput?.focus?.();
            }, 0);
        }
    };

    const handlePartKeyUp = (lineIndex: number, partIndex: number, event: React.KeyboardEvent<HTMLInputElement>) => {
        // 如果整行都为空，删除该行（除了第一行）
        if (event.key === 'Backspace' && lineIndex > 0) {
            setTimeout(() => {
                const hasContent = locationLines[lineIndex].some(p => p.trim().length > 0);
                if (!hasContent) {
                    setLocationLines((prev) => prev.filter((_, i) => i !== lineIndex));
                    setTimeout(() => {
                        const prevInput = lineInputRefs.current[lineIndex - 1]?.[3];
                        prevInput?.focus?.();
                    }, 0);
                }
            }, 0);
        }
    };

    return (
        <Modal
            title={t('wms.locationManagement.modal.title')}
            open={visible}
            onOk={handleOk}
            onCancel={onCancel}
            confirmLoading={loading}
            okText={t('wms.locationManagement.modal.okText')}
            cancelText={t('wms.locationManagement.modal.cancelText')}
            width={760}
        >
            <div className="mb-2 text-red-500">
                {t('wms.locationManagement.modal.tips')}
            </div>
            <div className="mb-3 text-gray-500 text-sm">
                {t('wms.locationManagement.modal.guide')}
            </div>

            <div className="flex items-center gap-6 mb-4">
                <div className={rowClassName}>
                    <span className={labelClassName}>
                        {t('wms.locationManagement.modal.hub')}
                    </span>
                    <Input value={hubName} disabled style={{ width: 200 }} />
                </div>
                <div className={rowClassName}>
                    <span className={labelClassName}>
                        {t('wms.locationManagement.modal.warehouse')}
                    </span>
                    <Select
                        value={selectedWarehouseId}
                        onChange={(value) => setSelectedWarehouseId(value)}
                        style={{ width: 200 }}
                        disabled={Number(currentWarehouse?.siteType) === SiteType.WAREHOUSE}
                        options={warehouseOptions}
                    />
                </div>
            </div>

            <div className="mb-2">
                <span className="text-gray-500">{t('wms.locationManagement.modal.locationNo')}</span>
            </div>
            <div className="mb-2 text-gray-500 text-sm">
                {t('wms.locationManagement.modal.linePlaceholder')}
            </div>
            <div className="border border-gray-200 rounded-lg overflow-hidden">
                <div className="px-3" style={{ height: containerHeight, overflowY: 'auto' }}>
                    {locationLines.map((parts, lineIndex) => {
                        // 初始化该行的ref数组
                        if (!lineInputRefs.current[lineIndex]) {
                            lineInputRefs.current[lineIndex] = [];
                        }
                        return (
                            <div
                                key={`line-${lineIndex}`}
                                className="flex items-center"
                                style={{
                                    height: rowHeight,
                                    borderBottom: lineIndex < locationLines.length - 1 ? '1px solid #e5e7eb' : '1px solid transparent'
                                }}
                            >
                                <span className="text-gray-400 text-sm w-8 text-right mr-3 select-none">
                                    {lineIndex + 1}
                                </span>
                                <div className="flex items-center gap-1 flex-1">
                                    {parts.map((part, partIndex) => {
                                        // 计算当前输入框之前所有输入框的累计字符数、本行总字符数
                                        const charsBeforeCurrent = parts.slice(0, partIndex).join('').length;
                                        const totalChars = parts.join('').length;
                                        const charsUpToCurrent = parts.slice(0, partIndex + 1).join('').length;
                                        // 前面格子已满20字符时，当前及后续格子置灰；本行已满20字符时，前面未参与的空格也置灰
                                        const isDisabled =
                                            charsBeforeCurrent >= 20 ||
                                            (totalChars >= 20 && part.trim().length === 0 && charsBeforeCurrent === 0);
                                        const showSeparator = partIndex < 3 && charsUpToCurrent < 20;

                                        return (
                                            <React.Fragment key={`part-${partIndex}`}>
                                                <Input
                                                    value={part}
                                                    disabled={isDisabled}
                                                    onChange={(e) => !isDisabled && handlePartChange(lineIndex, partIndex, e.target.value)}
                                                    onKeyDown={(e) => !isDisabled && handlePartKeyDown(lineIndex, partIndex, e)}
                                                    onKeyUp={(e) => !isDisabled && handlePartKeyUp(lineIndex, partIndex, e)}
                                                    onPaste={(e) => {
                                                        if (isDisabled) return;
                                                        e.preventDefault();
                                                        const pastedText = e.clipboardData.getData('text').replace(/[^a-zA-Z0-9]/g, '');
                                                        if (pastedText) {
                                                            handlePartChange(lineIndex, partIndex, pastedText);
                                                        }
                                                    }}
                                                    onInput={(e) => {
                                                        if (isDisabled) return;
                                                        const target = e.target as HTMLInputElement;
                                                        const value = target.value;
                                                        const filteredValue = value.replace(/[^a-zA-Z0-9]/g, '');
                                                        if (value !== filteredValue) {
                                                            target.value = filteredValue;
                                                            handlePartChange(lineIndex, partIndex, filteredValue);
                                                        }
                                                    }}
                                                    onFocus={() => {
                                                        if (!isDisabled) {
                                                            setFocusedLineIndex(lineIndex);
                                                            setFocusedPartIndex(partIndex);
                                                        }
                                                    }}
                                                    onBlur={() => {
                                                        if (focusedLineIndex === lineIndex) {
                                                            setFocusedLineIndex(null);
                                                        }
                                                    }}
                                                    bordered={false}
                                                    ref={(el) => {
                                                        if (!lineInputRefs.current[lineIndex]) {
                                                            lineInputRefs.current[lineIndex] = [];
                                                        }
                                                        lineInputRefs.current[lineIndex][partIndex] = el;
                                                    }}
                                                    className={`text-sm text-center ${isDisabled ? '!bg-gray-100 !text-gray-400 cursor-not-allowed' : ''}`}
                                                    style={{
                                                        flex: 1,
                                                        borderBottom: focusedLineIndex === lineIndex && focusedPartIndex === partIndex && !isDisabled ? '1px solid #e5e7eb' : '1px solid transparent',
                                                        borderRadius: 0
                                                    }}
                                                />
                                                {showSeparator ? <span className="text-gray-400 text-lg select-none">-</span> : null}
                                            </React.Fragment>
                                        );
                                    })}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        </Modal>
    );
};

export default AddLocationModal;

